import React, { useEffect, useState } from 'react';
import { Users, Activity, ShoppingCart, ArrowDownLeft, ArrowUpRight, Tag } from 'lucide-react';
import { useCurrency } from '../../../../hooks/useCurrency';
import api from '../../../../api/axios';

interface KeyFiguresDTO {
  totalUsers: number;
  onlineUsers: number;
  totalTrades: number;
  listedItems: number;
  totalDeposits: number;
  totalDepositsValueMinor: number;
  totalWithdrawals: number;
  totalWithdrawalsValueMinor: number;
}

const SystemStats = () => {
  const { convertFromUSD, currencySymbol, formatAmountWithoutCurrency } = useCurrency();
  const [data, setData] = useState<KeyFiguresDTO | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch statistics from API
    api.get<KeyFiguresDTO>('/key-figures')
    .then(res => {
      setData(res.data);
    })
    .catch(err => console.error('Failed to fetch system stats', err))
    .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-1 gap-4 opacity-50">
          {[...Array(6)].map((_, i) => (
              <div key={i} className="h-24 bg-neutral-900/40 animate-pulse rounded-lg border border-neutral-800" />
          ))}
        </div>
    );
  }

  const stats = [
    {
      label: 'Users Total',
      value: data.totalUsers.toLocaleString(),
      icon: <Users className="text-blue-400" size={18} />
    },
    {
      label: 'Users Online',
      value: data.onlineUsers.toLocaleString(),
      icon: <Activity className="text-emerald-400" size={18} />
    },
    {
      label: 'Trades',
      value: data.totalTrades.toLocaleString(),
      icon: <ShoppingCart className="text-purple-400" size={18} />
    },
    {
      label: 'Listed Items',
      value: data.listedItems.toLocaleString(),
      icon: <Tag className="text-orange-400" size={18} />
    },
    {
      label: 'Deposits',
      value: data.totalDeposits.toLocaleString(),
      money: data.totalDepositsValueMinor / 100, // Convert minor to major unit
      icon: <ArrowDownLeft className="text-emerald-400" size={18} />
    },
    {
      label: 'Withdraws',
      value: data.totalWithdrawals.toLocaleString(),
      money: data.totalWithdrawalsValueMinor / 100, // Convert minor to major unit
      icon: <ArrowUpRight className="text-red-400" size={18} />
    },
  ];

  return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-1 gap-4">
        {stats.map((stat, idx) => (
            <div
                key={idx}
                className="bg-neutral-900/40 backdrop-blur-md border border-neutral-800 p-4 rounded-lg shadow-sm hover:border-neutral-700 transition-all"
            >
              <div className="flex justify-between items-start mb-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-neutral-500">
              {stat.label}
            </span>
                {stat.icon}
              </div>

              <div className="flex items-baseline justify-between gap-2">
            <span className="text-xl font-black text-white leading-none">
              {stat.value}
            </span>
                {stat.money !== undefined && (
                    <span className="text-xs font-mono font-bold text-neutral-400 whitespace-nowrap">
                {currencySymbol}{formatAmountWithoutCurrency(convertFromUSD(stat.money))}
              </span>
                )}
              </div>
            </div>
        ))}
      </div>
  );
};

export default SystemStats;